<template>
    <div class="personal-info-container">
        <!-- 主内容区域 -->
        <h1 class="title">身材类型</h1>

        <div class="main-content">
            <div class="form-content">
                <!-- 选中的身体类型大图显示 -->
                <div class="selected-body-display">
                    <div class="selected-body-container">
                        <img
                                :src="selectedBodyImage"
                                :alt="displayName(bodyType)"
                                class="selected-body-figure"
                        />
                    </div>
                    <div class="selected-body-name">
                        <span>{{ selectedTitle }}</span>
                    </div>
                    <div class="selected-body-desc">{{ selectedDescription }}</div>
                </div>

                <!-- 身体类型选择列表 -->
                <div class="bodies" ref="bodiesContainer">
                        <div
                                v-for="(b, i) in bodies"
                                :key="i"
                                :ref="el => { if (el) bodyCardRefs[i] = el }"
                                class="body-card"
                                :class="{ active: bodyType === b.name }"
                                @click="selectBodyType(b.name, i)"
                        >
                            <img :src="!bodyType || bodyType === b.name ? (b.imageCheck || b.image) : b.image" :alt="displayName(b.name)" class="figure"/>
                            <div class="body-name">{{ displayName(b.name) }}</div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        
        <!-- 底部操作 -->
        <div class="bottom-actions">
            <button class="btn-secondary" @click="goBack">
              <span class="btn-icon"><img :src="backIcon"></span>
              <span class="btn-text">上一步</span>
            </button>
            <button class="btn-primary" :disabled="!isFormValid" @click="goNext">
              <span class="btn-icon"><img :src="confirmIcon"></span>
              <span class="btn-text">确定</span>
            </button>
        </div>
</template>

<script setup>
import { ref, computed, watch, nextTick, onMounted } from 'vue'
import hourglassImg from "@/assets/images/webapp/hourglass.svg";
import hourglassImgCheck from "@/assets/images/webapp/hourglassImgCheck.svg";
const confirmIcon = require('@/assets/images/svg/confirm.svg')
const backIcon = require('@/assets/images/svg/back.svg')
import triangleImg from "@/assets/images/webapp/triangle.svg";
import triangleImgCheck from "@/assets/images/webapp/triangleCheck.svg";
import invertedTriangle from "@/assets/images/webapp/invertedTriangle.svg";
import invertedTriangleCheck from "@/assets/images/webapp/invertedTriangleCheck.svg";
import shapedBody from "@/assets/images/webapp/shapedbody.svg";
import shapedBodyCheck from "@/assets/images/webapp/shapedbodyCheck.svg";
import rectangle from "@/assets/images/webapp/rectangle.svg";
import rectangleCheck from "@/assets/images/webapp/rectangleCheck.svg";
import notCheck from "@/assets/images/webapp/notCheck.svg";
const bodies = [
    {name: 'hourglass', image: hourglassImg, imageCheck: hourglassImgCheck},
    {name: 'Triangle', image: triangleImg, imageCheck: triangleImgCheck},
    {name: 'Inverted triangle', image: invertedTriangle, imageCheck: invertedTriangleCheck},
    {name: 'O-shaped body', image: shapedBody, imageCheck: shapedBodyCheck},
    {name: 'rectangle', image: rectangle, imageCheck: rectangleCheck}
]
// 定义 emits
const emit = defineEmits(['next', 'back', 'validation-change', 'data-change'])

// 定义 props 来接收回填数据
const props = defineProps({
    initialData: {
        type: Object,
        default: () => ({})
    }
})
const bodyType = ref(null)

// 容器和卡片的 ref
const bodiesContainer = ref(null)
const bodyCardRefs = ref([])

// 响应式数据
const email = ref('')
const name = ref('')
const height = ref('')
const weight = ref('')
const isLoading = ref(false)
const bodyImageFor = (b) => {
    // If the body is the currently selected one, use the checked image if available
    const selected = bodyType.value === b.name
    if (selected && b.imageCheck) return b.imageCheck
    return b.image
}

// 显示用中文名
const displayName = (key) => ({
    'hourglass': '沙漏形',
    'Triangle': '梨形',
    'Inverted triangle': 'T形',
    'O-shaped body': '苹果形',
    'rectangle': 'H形'
}[key] || key)

// 描述文案
const bodyDesc = (key) => ({
    'hourglass': '肩部、腰部和臀部有明显曲线，腰部较细，整体曲线平衡自然。',
    'Triangle': '臀部较宽，腰部纤细，上半身较为纤细，脂肪主要集中在臀部和大腿。',
    'Inverted triangle': '肩膀较宽，腰部和臀部较窄，腿部较细，整体身形呈直线感。',
    'O-shaped body': '肩部、腰部和臀部接近，缺乏明显的腰线，整体身形直线感较强。',
    'rectangle': '上半身较为宽广，腰线不明显，腿部较细，脂肪集中在上半身。'
}[key] || '')

// 选中/未选中时的标题和描述
const selectedTitle = computed(() => {
    return bodyType.value ? displayName(bodyType.value) : '请选择您的身材类型'
})
const selectedDescription = computed(() => {
    return bodyType.value ? bodyDesc(bodyType.value) : '（点按可查看不同特征）'
})

// 计算选中身体类型的大图
const selectedBodyImage = computed(() => {
    const selectedBody = bodies.find(b => b.name === bodyType.value)
    return selectedBody ? (selectedBody.imageCheck || selectedBody.image) : notCheck
})
// 计算属性
const isFormValid = computed(() => {
    return bodyType.value !== null && bodyType.value !== ''
})

// 监听验证状态变化并通知父组件
watch(isFormValid, (newValue) => {
    nextTick(() => {
        emit('validation-change', newValue)
    })
})

// 监听props变化，更新本地数据
watch(() => props.initialData, (newInitialData) => {
    if (newInitialData && newInitialData.bodyType !== undefined && newInitialData.bodyType !== null) {
        bodyType.value = newInitialData.bodyType
        console.log('Step2 (bodyType): watch 恢复数据:', bodyType.value)
    }
}, { deep: true, immediate: true })

// 选择身体类型并滚动到视野中
const selectBodyType = (name, index) => {
    bodyType.value = name
    
    // 实时保存身体类型选择
    emit('data-change', {
        bodyType: bodyType.value
    })
    
    // 使用 nextTick 确保 DOM 完全更新后再滚动
    nextTick(() => {
        nextTick(() => {
            setTimeout(() => {
                const container = bodiesContainer.value
                const card = bodyCardRefs.value[index]

                if (!container || !card) {
                    return
                }

                // 获取当前滚动位置和容器尺寸
                const currentScroll = container.scrollLeft
                const containerWidth = container.clientWidth

                // 获取卡片的位置（相对于滚动内容）
                const cardOffsetLeft = card.offsetLeft
                const cardWidth = card.offsetWidth

                // 选中状态需要的额外空间（边框、阴影等）
                const extraSpace = 15

                // 计算卡片在容器中的可见范围
                const cardLeft = cardOffsetLeft
                const cardRight = cardLeft + cardWidth
                const visibleLeft = currentScroll
                const visibleRight = currentScroll + containerWidth

                let targetScroll = currentScroll

                // 只在卡片被遮挡时才滚动，且只滚动最小距离
                // 如果卡片左侧被遮挡或太靠近左边缘
                if (cardLeft < visibleLeft + extraSpace) {
                    // 只滚动到让卡片左侧有足够空间即可
                    targetScroll = cardLeft - extraSpace
                }
                // 如果卡片右侧被遮挡或太靠近右边缘
                else if (cardRight > visibleRight - extraSpace) {
                    // 只滚动到让卡片右侧有足够空间即可
                    targetScroll = cardRight - containerWidth + extraSpace
                }
                // 如果卡片完全可见，不滚动

                // 限制在有效范围内
                const maxScroll = Math.max(0, container.scrollWidth - container.clientWidth)
                targetScroll = Math.max(0, Math.min(targetScroll, maxScroll))

                // 只在需要滚动时才执行
                if (Math.abs(targetScroll - currentScroll) > 1) {
                    container.scrollTo({
                        left: targetScroll,
                        behavior: 'smooth'
                    })
                }
            }, 150)
        })
    })
}

// 组件挂载后手动触发一次验证状态检查，并滚动到选中的卡片
onMounted(() => {
    // 从 props.initialData 恢复身体类型
    if (props.initialData && props.initialData.bodyType !== undefined) {
        bodyType.value = props.initialData.bodyType
        console.log('Step2 (bodyType): 从initialData恢复数据:', bodyType.value)
    }

    nextTick(() => {
        emit('validation-change', isFormValid.value)
        // 如果有初始数据，也要触发数据保存
        if (props.initialData && props.initialData.bodyType) {
            emit('data-change', {
                bodyType: bodyType.value
            })
        }

        // 初始状态：确保滚动位置为0，让5个卡片都可见
        if (bodiesContainer.value) {
            nextTick(() => {
                setTimeout(() => {
                    const container = bodiesContainer.value
                    if (container) {
                        // 初始状态设置为0，让所有卡片都可见
                        container.scrollLeft = 0

                        // 如果有初始选中的身型，检查是否需要滚动
                        const selectedIndex = bodies.findIndex(b => b.name === bodyType.value)
                        if (selectedIndex !== -1 && bodyCardRefs.value[selectedIndex]) {
                            const card = bodyCardRefs.value[selectedIndex]
                            const containerWidth = container.clientWidth
                            const cardOffsetLeft = card.offsetLeft
                            const cardWidth = card.offsetWidth
                            const extraSpace = 15

                            const cardLeft = cardOffsetLeft
                            const cardRight = cardLeft + cardWidth
                            const visibleLeft = 0
                            const visibleRight = containerWidth

                            // 只在卡片被遮挡时才滚动
                            let targetScroll = 0
                            if (cardLeft < visibleLeft + extraSpace) {
                                targetScroll = cardLeft - extraSpace
                            } else if (cardRight > visibleRight - extraSpace) {
                                targetScroll = cardRight - containerWidth + extraSpace
                            }

                            const maxScroll = Math.max(0, container.scrollWidth - container.clientWidth)
                            targetScroll = Math.max(0, Math.min(targetScroll, maxScroll))

                            // 只在需要滚动时才设置
                            if (Math.abs(targetScroll) > 1) {
                                container.scrollLeft = targetScroll
                            }
                        }
                    }
                }, 100)
            })
        }
    })
})

// 验证邮箱格式
const validateEmailFormat = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
}

// 验证邮箱输入
const validateEmail = () => {
    // 可以在这里添加实时验证逻辑
}

// 验证姓名输入
const validateName = () => {
    // 可以在这里添加实时验证逻辑
}

// 验证身高输入
const validateHeight = () => {
    if (parseFloat(height.value) < 0) {
        height.value = ''
    }
}

// 验证体重输入
const validateWeight = () => {
    if (parseFloat(weight.value) < 0) {
        weight.value = ''
    }
}



// 返回上一页（这里可以发射事件给父组件处理）
const goBack = () => {
    // 在返回前确保保存当前数据
    emit('data-change', {
        bodyType: bodyType.value
    })
    // 延迟一下再返回，确保数据保存完成
    nextTick(() => {
        emit('back')
    })
}

// 进入下一页
const goNext = () => {
    if (!isFormValid.value) return

    const stepData = {
        bodyType: bodyType.value
    }

    emit('next', stepData)
}

// 为了兼容，保持handleSubmit方法名
const handleSubmit = goNext

// 暴露方法给父组件调用
defineExpose({
    handleSubmit
})
</script>

<style scoped>
/* 主容器 - 全屏深色背景 + 波浪纹理 */
.personal-info-container {
    width: 100vw;
    height: 100dvh; /* 动态视口高度，更适合移动端 */
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    /*justify-content: center;*/
    position: fixed;
    top: 0;
    left: 0 !important;
    right: 0;
    bottom: 0;
    overflow: hidden;
    box-sizing: border-box;
}

/* 标题样式 */
.title {
    color: rgba(255, 255, 255, 0.95);
    font-size: 24px;
    margin-top: 10%;
    font-weight: 1000;
    letter-spacing: 0.5px;
    line-height: 1.2;
    text-align: center;
    flex-shrink: 0;
}

/* 主内容卡片 - 居中圆角卡片 */
.main-content {
    width: calc(100% - 32px);
    max-width: 420px;
    height: auto;
    max-height: calc(80dvh - 120px); /* 为底部按钮留出空间 */
    background: rgba(25, 25, 25, 0.95);
    border-radius: 24px;
    padding: 28px 20px 24px;
    margin-top: 5%;
    box-shadow:
        0 20px 60px rgba(0, 0, 0, 0.5),
        0 0 0 1px rgba(255, 255, 255, 0.06);
    backdrop-filter: blur(20px);
    display: flex;
    flex-direction: column;
    position: relative;
    overflow: hidden;
}
.main-content::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 24px;
    padding: 1px;
    background: linear-gradient(135deg,
    rgba(255, 255, 255, 1),
    rgba(204, 204, 204, 0.8),
    rgba(204, 204, 204, 0.4),
    rgba(204, 204, 204, 0.4),
    rgba(204, 204, 204, 0.8),
    rgba(255, 255, 255, 0.9));
    -webkit-mask:
            linear-gradient(#fff 0 0) content-box,
            linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    mask:
            linear-gradient(#fff 0 0) content-box,
            linear-gradient(#fff 0 0);
    mask-composite: exclude;
    pointer-events: none;
    z-index: -1;
}

/* 卡片包装器 - 圆角半透明卡片 */
.card-wrapper {
    background: rgba(35, 35, 35, 0.9);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 22px;
    padding: 36px 20px 24px;
    max-width: 380px;
    width: 100%;
    backdrop-filter: blur(25px);
    box-shadow: 0 20px 60px rgba(0,0,0,0.6);
    position: relative;
    z-index: 1;
    overflow: hidden;
    display: flex;
    flex-direction: column;
}

/* 表单内容区域 */
.form-content {
    display: flex;
    flex-direction: column;
    gap: 27px;
    align-items: center;
    flex: 1;
    overflow: visible; /* 改为 visible，不让它滚动 */
    /* 预留空间，避免被底部操作条或圆角裁切 */
    /*padding-bottom: calc(90px + env(safe-area-inset-bottom, 0px));*/
}

.form-content::-webkit-scrollbar {
    display: none;
}

/* 选中的身体类型大图显示 */
.selected-body-display {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 5px;
    flex-shrink: 0;
    margin-top: -1%;
}

.selected-body-container {
    width: 170px;
    height: 240px;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    border-radius: 18px;
    /*backdrop-filter: blur(10px);*/

}

.selected-body-figure {
    width: 160px;
    height: 210px;
    object-fit: contain;
    filter: brightness(1.15) drop-shadow(0 8px 24px rgba(166,245,93,0.3));
    position: relative;
    z-index: 2;
}

.selected-body-name {
    display: flex;
    align-items: center;
    gap: 8px;
    color: rgba(255, 255, 255);
    font-size: 17px;
    font-weight: 600;
}
.selected-body-desc {
    color: rgba(255,255,255,0.8);
    font-size: 14px;
    line-height: 1.4;
    text-align: center;
    max-width: 280px;
    padding: 0 10px;
    /* 默认两行高度（1.4 * 2 = 2.8） */
    min-height: 2.8em;
    display: -webkit-box;
    -webkit-line-clamp: 2; /* 最多两行 */
    line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    white-space: normal;
}

.selected-body-tip {
    margin-top: 8px;
    color: rgba(255, 255, 255, 0.9);
    font-size: 12px;
    line-height: 1.4;
    text-align: center;
    max-width: 280px;
    padding: 0 10px;
}

.selected-body-tip .tip-line1 { font-size: 14px; font-weight: 600; color: rgba(255,255,255,0.95); }
.selected-body-tip .tip-line2 { font-size: 12px; color: rgba(255,255,255,0.8); margin-top: 2px; }

.body-indicator {
    color: #A6F55D;
    font-size: 13px;
}

/* 左右侧箭头按钮 */
.side-arrow-button {
    width: 50px;
    height: 50px;
    background: rgba(76, 175, 80, 0.8);
    border: none;
    border-radius: 50%;
    cursor: pointer;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    backdrop-filter: blur(10px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
    flex-shrink: 0;
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    z-index: 10;
}

.side-arrow-button:hover {
    background: rgba(76, 175, 80, 1);
    transform: translateY(-50%) scale(1.1);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.3);
}

.side-arrow-button:active:not(.disabled) {
    transform: translateY(-50%) scale(0.95);
}

.side-arrow-button.disabled {
    opacity: 0.4;
    cursor: not-allowed;
    background: rgba(100, 100, 100, 0.3);
}

.side-arrow-button.disabled:hover {
    transform: translateY(-50%);
    background: rgba(100, 100, 100, 0.3);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

.left-arrow {
    left: -25px;
}

.right-arrow {
    right: -25px;
}

/* 箭头图标 */
.arrow-icon {
    width: 20px;
    height: 20px;
    color: white;
}


.sub-title {
    color: rgba(255, 255, 255, 0.9);
    font-weight: 600;
    font-size: 18px;
    text-transform: lowercase;
    letter-spacing: 0.5px;
    margin-bottom: 20px;
}

/* 横向滚动容器 */
.bodies-scroll-wrapper {
    width: 100%;
    overflow-x: auto;
    overflow-y: hidden;
    padding: 4px 0 8px;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
}
.bodies-scroll-wrapper::-webkit-scrollbar {
    display: none;
}

.bodies {
    display: flex;
    /*width: 100%;*/
    overflow-x: auto;
    overflow-y: visible;
    -webkit-overflow-scrolling: touch;
    scroll-snap-type: x proximity;
    scrollbar-width: none; /* Firefox */
    -ms-overflow-style: none; /* IE/Edge */
    flex-shrink: 0;
    box-sizing: border-box;
    position: relative;
}

.bodies::-webkit-scrollbar {
    display: none; /* Chrome/Safari */
}

.body-card {
    width: 62px;
    min-width: 62px;
    flex-shrink: 0;
    height: 110px;
    margin: 0 1px 8px 1px; /* 最小化左右间距，让5个卡片都能显示 */
    scroll-snap-align: center;
    border-radius: 12px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 4px;
    cursor: pointer;
    /*background: rgba(255, 255, 255, 0.04);*/
    /*border: 2px solid rgba(255, 255, 255, 0.12);*/
    transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
    box-sizing: border-box;
}


.body-card.active {
    border: 1px solid #A6F55D;
    /*background: rgba(166, 245, 93, 0.18);*/
    box-shadow:
        0 0 0 1px rgba(166, 245, 93, 0.4),
        0 4px 12px rgba(0,0,0,0.25),
        0 0 8px rgba(166, 245, 93, 0.15);
    /*transform: translateY(-1px) scale(1.02); !* 减小缩放避免遮挡 *!*/
    transform-origin: center;
}

.body-card:hover:not(.active) {
    background: rgba(255, 255, 255, 0.1);
    border-color: rgba(255, 255, 255, 0.2);
    transform: scale(1.02);
}

.figure {
    width: 38px;
    height: 68px;
    object-fit: contain;
    border-radius: 4px;
    opacity: 0.95;
}

/* 小屏高度适配，缩小元素以完整显示 */
@media (max-height: 720px) {
    .title {
        font-size: 20px;
        margin-top: 6%;
    }
    .main-content {
        max-height: calc(75dvh - 120px);
        margin-top: 3%;
        padding: 20px 16px 18px;
    }
    .selected-body-container {
        width: 130px;
        height: 180px;
    }
    .selected-body-figure {
        width: 120px;
        height: 160px;
    }
    .selected-body-name {
        font-size: 15px;
        margin-top: 2px;
    }
    .selected-body-desc {
        font-size: 10px;
        min-height: 2.4em;
        margin-top: 0;
    }
    .form-content {
        gap: 18px;
    }
    .body-card {
        height: 95px;
        width: 62px;
        min-width: 62px;
        margin: 0 2px 8px 2px; /* 减小间距 */
    }
    .figure {
        width: 36px;
        height: 64px;
    }
    .body-name {
        font-size: 10px;
    }
}

/* 更小屏幕的额外适配 */
@media (max-height: 667px) {
    .title {
        font-size: 18px;
        margin-top: 4%;
        margin-bottom: 20px;
    }
    .main-content {
        max-height: calc(70dvh - 100px);
        margin-top: 2%;
        padding: 16px 14px 16px;
    }
    .selected-body-container {
        width: 110px;
        height: 150px;
    }
    .selected-body-figure {
        width: 100px;
        height: 135px;
    }
    .selected-body-name {
        font-size: 14px;
        margin-top: 0;
    }
    .selected-body-desc {
        font-size: 9px;
        min-height: 2.2em;
        padding: 0 8px;
    }
    .form-content {
        gap: 14px;
    }
    .body-card {
        height: 88px;
        width: 58px;
        min-width: 58px;
        margin: 0 2px 8px 2px; /* 减小间距 */
        padding: 6px 4px;
    }
    .figure {
        width: 32px;
        height: 58px;
    }
    .body-name {
        font-size: 9px;
    }
    .bottom-actions {
        padding: 12px 16px calc(12px + env(safe-area-inset-bottom, 0px));
        bottom: 3%;
    }
    .btn-secondary, .btn-primary {
        height: 44px;
        font-size: 15px;
    }
}

.body-name {
    color: rgba(255, 255, 255, 0.85);
    font-size: 11px;
    font-weight: 500;
    text-align: center;
    line-height: 1.2;
}

.body-card.active .body-name {
    color: #A6F55D;
    font-weight: 700;
}
/* 大屏幕适配 - 让卡片更大更美观 */
@media (min-width: 400px) {
    .body-card {
        width: 65px;
        min-width: 65px;
    }
}
/* 大屏幕适配 - 让卡片更大更美观 */
@media (min-width: 480px) {
    .body-card {
        width: 70px;
        min-width: 70px;
        height: 115px;
        margin: 0 3px 8px 3px;
        padding: 6px;
        gap: 8px;
    }

    .figure {
        width: 42px;
        height: 72px;
    }

    .body-name {
        font-size: 12px;
    }

    .bodies {
        padding: 0 6px;
    }
}

/* 更大屏幕适配 */
@media (min-width: 600px) {
    .body-card {
        width: 75px;
        min-width: 75px;
        height: 120px;
        margin: 0 4px 8px 4px;
        padding: 8px;
    }

    .figure {
        width: 45px;
        height: 75px;
    }

    .body-name {
        font-size: 13px;
    }

    .bodies {
        padding: 0 8px;
    }
}

/* 底部操作条 */
.bottom-actions {
    position: fixed;
    left: 0;
    right: 0;
    bottom: 5%;
    padding: 16px 20px calc(16px + env(safe-area-inset-bottom, 0px));
    display: flex;
    gap: 12px;
    justify-content: space-between;
    /*background: linear-gradient(180deg, rgba(10,10,10,0) 0%, rgba(10,10,10,0.8) 40%, rgba(10,10,10,0.95) 100%);*/
    /*backdrop-filter: blur(16px);*/
    z-index: 100;
    pointer-events: none;
}

.bottom-actions > * {
    pointer-events: auto;
}
.btn-secondary, .btn-primary {
    flex: 1;
    height: 48px;
    border-radius: 24px;
    border: none;
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
}
.btn-secondary { 
    background: rgba(255,255,255,0.12); 
    color: rgba(255,255,255,0.9); 
    border: 1px solid rgba(255,255,255,0.2);
}
.btn-secondary:active {
    background: rgba(255,255,255,0.18);
    transform: scale(0.98);
}
.btn-primary { 
    background: #A6F55D; 
    color: #111; 
    font-weight: 600;
    box-shadow: 0 4px 16px rgba(166,245,93,0.3);
}
.btn-primary:active {
    background: #95E04C;
    transform: scale(0.98);
}
.btn-primary:disabled { 
    opacity: 0.4; 
    cursor: not-allowed;
    box-shadow: none;
}

.btn-icon {
    display: flex;
    align-items: center;
    justify-content: center;
}

.btn-icon img {
    display: block;
    width: 20px;
    height: 20px;
    vertical-align: middle;
}

.btn-text {
    display: flex;
    align-items: center;
    line-height: 1;
}

</style>

